import { formatDistance, differenceInMinutes } from 'date-fns'

import type { Service, ChannelType } from '../graphqlEnums'

import type { CommentsConfig } from '../CommunityConfig'

type ChannelInfo = {
  service?: Service
  type?: ChannelType
}

/**
 * Checks if a channel uses realtime sync (webhook/firehose) with error handling.
 *
 * @param channel - The channel object with service and type properties
 * @param config - The comments configuration object
 * @returns true if the channel uses realtime sync, false otherwise or on error
 */
export function getIsRealtimeSync(
  channel: ChannelInfo | null | undefined,
  config: Pick<CommentsConfig, 'isRealtimeSync'>,
): boolean {
  if (!channel?.service || !channel?.type) {
    return false
  }

  try {
    return config.isRealtimeSync({
      service: channel.service,
      type: channel.type,
    })
  } catch {
    return false
  }
}

/**
 * Calculates the time ago string from a last checked timestamp.
 *
 * @param lastCheckedAt - The timestamp (ISO string or null) when the channel was last checked
 * @param now - The current time to compare against
 * @returns A formatted string like "2 hours ago" or "recently" if no timestamp
 */
export function getTimeAgo(
  lastCheckedAt: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!lastCheckedAt) {
    return 'recently'
  }

  const minutes = differenceInMinutes(now, new Date(lastCheckedAt))
  if (minutes >= 45 && minutes < 60) {
    return `${minutes} minutes ago`
  }

  return formatDistance(new Date(lastCheckedAt), now, { addSuffix: true })
}

/**
 * Formats the next sync time range for display.
 *
 * @param nextUpdateAt - The timestamp (ISO string or null) when the next sync is scheduled
 * @param now - The current time to compare against
 * @param formatFn - Function to format the time range (defaults to getFormattedNextSyncTimeRange from dateHelpers)
 * @returns A formatted string describing when the next sync will occur
 */
export function getNextSyncTimeRange(
  nextUpdateAt: string | null | undefined,
  now: Date = new Date(),
  formatFn: (date: Date, now: Date) => string,
): string {
  if (!nextUpdateAt) {
    return 'to run soon'
  }

  return formatFn(new Date(nextUpdateAt), now)
}

/**
 * Calculates the number of minutes since the last sync.
 *
 * @param lastCheckedAt - The timestamp (ISO string or null) when the channel was last checked
 * @param now - The current time to compare against
 * @returns Number of minutes since last sync, or null if lastCheckedAt is not provided
 */
export function getMinutesSinceLastSync(
  lastCheckedAt: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!lastCheckedAt) {
    return null
  }

  return differenceInMinutes(now, new Date(lastCheckedAt))
}
